import { useQuery } from "@tanstack/react-query";
import { PostgrestError } from "@supabase/supabase-js";
import { buildReportTicketsQuery } from "@west-tennessee-consulting/supabase";
import { logger } from "@/lib/logger";
import { getSupabase } from "../utils";
import { processTicketsData } from "@/lib/utils/crm/ticket-helpers";
import type { RawTicketData } from "@/lib/utils/crm/ticket-types";
import type { ReportPagination } from "./report-helpers";
import {
  isActiveTicketStatus,
  isResolvedTicketStatus,
} from "@/lib/constants/ticket-statuses";
import type { Ticket_Status } from "@/src/gql/graphql";

export interface TicketReportFilters {
  date_from?: string;
  date_to?: string;
  status?: string[];
  priority?: string[];
  category?: string[];
  assigned_to?: string;
  contact_id?: string;
  is_internal?: boolean;
}

interface TicketMetricsRow {
  id: string;
  status: string;
  priority: string;
  category: string | null;
  created_at: string;
  resolved_at: string | null;
  contact_id: string | null;
}

type TicketMetricsResult = { data: TicketMetricsRow[] | null; error: PostgrestError | null };

interface TicketMetricsQueryBuilder extends PromiseLike<TicketMetricsResult> {
  gte: (column: string, value: string) => TicketMetricsQueryBuilder;
  lte: (column: string, value: string) => TicketMetricsQueryBuilder;
  eq: (column: string, value: string | boolean) => TicketMetricsQueryBuilder;
  in: (column: string, values: readonly string[]) => TicketMetricsQueryBuilder;
}

function applyTicketMetricsFilters(
  query: TicketMetricsQueryBuilder,
  filters: TicketReportFilters,
  isClientUser: boolean,
  clientId?: string
): TicketMetricsQueryBuilder {
  let next = query;
  if (filters.date_from) next = next.gte("created_at", filters.date_from);
  if (filters.date_to) next = next.lte("created_at", filters.date_to);
  if (filters.status?.length) next = next.in("status", filters.status);
  if (filters.priority?.length) next = next.in("priority", filters.priority);
  if (filters.category?.length) next = next.in("category", filters.category);
  if (filters.contact_id) next = next.eq("contact_id", filters.contact_id);
  if (filters.assigned_to) next = next.eq("assigned_to", filters.assigned_to);
  if (filters.is_internal !== undefined) next = next.eq("is_internal", filters.is_internal);
  if (isClientUser && clientId) next = next.eq("contact_id", clientId);
  return next;
}

export function useTicketReportData(
  filters: TicketReportFilters,
  isClientUser: boolean,
  clientId?: string,
  pagination?: ReportPagination,
  isGroupingActive?: boolean
) {
  return useQuery({
    queryKey: ["ticket-reports", filters, isClientUser, clientId, pagination, isGroupingActive],
    queryFn: async () => {
      const supabase = getSupabase();
      const query = buildReportTicketsQuery(supabase, {
        dateFrom: filters.date_from,
        dateTo: filters.date_to,
        status: filters.status,
        priority: filters.priority,
        category: filters.category,
        assignedTo: filters.assigned_to,
        clientId: filters.contact_id || (isClientUser ? clientId : undefined),
        isInternal: filters.is_internal,
      });

      let countQuery = supabase.from("tickets").select("*", { count: "exact", head: true });
      if (isClientUser && clientId) {
        countQuery = countQuery.eq("contact_id", clientId);
      }

      const { count } = await countQuery;
      let data: RawTicketData[] = [];
      let error: PostgrestError | null = null;

      if (isGroupingActive) {
        const chunkSize = 1000;
        const totalCount = count || 0;

        if (totalCount > 0) {
          const chunks = Math.ceil(totalCount / chunkSize);
          const allData: RawTicketData[] = [];

          logger.info(`Fetching ${totalCount} tickets in ${chunks} chunks for grouping...`);

          for (let i = 0; i < chunks; i++) {
            const from = i * chunkSize;
            const to = Math.min(from + chunkSize - 1, totalCount - 1);
            const chunkQuery = query.order("created_at", { ascending: false }).range(from, to);
            const { data: chunkData, error: chunkError } = await chunkQuery;

            if (chunkError) {
              logger.error(`Error fetching chunk ${i + 1}/${chunks}`, chunkError);
              error = chunkError;
              break;
            }

            if (chunkData) {
              const typedChunk = chunkData as RawTicketData[];
              allData.push(...typedChunk);
              logger.info(`Loaded chunk ${i + 1}/${chunks}: ${typedChunk.length} items`);
            }
          }

          data = allData;
          logger.info(`Successfully loaded ${data.length} total tickets for grouping`);
        }
      } else {
        let dataQuery = query.order("created_at", { ascending: false });

        if (pagination) {
          const from = (pagination.page - 1) * pagination.limit;
          const to = from + pagination.limit - 1;
          dataQuery = dataQuery.range(from, to);
        }

        const result = await dataQuery;
        data = (result.data || []) as RawTicketData[];
        error = result.error;
      }

      if (error) {
        logger.error("Error fetching ticket reports", error);
        throw new Error("Failed to fetch ticket data");
      }

      const processedTickets = processTicketsData(data);
      const processedData = processedTickets.map((ticket) => {
        let resolution_time_hours: number | null = null;
        let sla_compliant = false;

        if (ticket.resolved_at && ticket.created_at) {
          const created = new Date(ticket.created_at);
          const resolved = new Date(ticket.resolved_at);
          resolution_time_hours = (resolved.getTime() - created.getTime()) / (1000 * 60 * 60);
          const slaHours =
            ticket.priority === "critical" ? 24 : ticket.priority === "high" ? 48 : 72;
          sla_compliant = resolution_time_hours <= slaHours;
        }

        return {
          id: ticket.id,
          title: ticket.title,
          status: ticket.status,
          priority: ticket.priority,
          category: ticket.category,
          contact_name: ticket.client_name,
          assigned_to_name: ticket.assigned_to_name,
          created_at: ticket.created_at,
          updated_at: ticket.created_at,
          resolved_at: ticket.resolved_at,
          resolution_time_hours,
          sla_compliant,
          is_internal: ticket.is_internal,
        };
      });

      return { data: processedData, total: count || 0 };
    },
    enabled: true,
  });
}

export function useTicketMetrics(
  filters: TicketReportFilters,
  isClientUser: boolean,
  clientId?: string
) {
  return useQuery({
    queryKey: ["ticket-metrics", filters, isClientUser, clientId],
    queryFn: async () => {
      const supabase = getSupabase();
      const baseQuery = supabase.from("tickets").select(`
                    id,
                    status,
                    priority,
                    category,
                    created_at,
                    resolved_at,
                    contact_id
                `) as unknown as TicketMetricsQueryBuilder;
      const query = applyTicketMetricsFilters(baseQuery, filters, isClientUser, clientId);

      const { data, error } = await query;

      if (error) {
        logger.error("Error fetching ticket metrics", error);
        throw new Error("Failed to fetch ticket metrics");
      }

      const rows = (data || []);
      const totalTickets = rows.length;
      const openTickets = rows.filter((t) =>
        isActiveTicketStatus(t.status as Ticket_Status)
      ).length;
      // Resolution count must include `closed` (resolved-then-closed), not just
      // `resolved` — counting `=== "resolved"` alone undercounts the rate.
      const resolvedTickets = rows.filter((t) =>
        isResolvedTicketStatus(t.status as Ticket_Status)
      ).length;
      const criticalTickets = rows.filter((t) => t.priority === "critical").length;
      const highPriorityTickets = rows.filter((t) => t.priority === "high").length;
      const resolvedWithTime = rows.filter((t) => t.resolved_at && t.created_at);
      const totalResolutionTime = resolvedWithTime.reduce((sum, ticket) => {
        const created = new Date(ticket.created_at);
        const resolved = new Date(ticket.resolved_at as string);
        return sum + (resolved.getTime() - created.getTime());
      }, 0);
      const avgResolutionHours =
        resolvedWithTime.length > 0
          ? totalResolutionTime / (resolvedWithTime.length * 1000 * 60 * 60)
          : 0;

      return {
        totalTickets,
        openTickets,
        resolvedTickets,
        criticalTickets,
        highPriorityTickets,
        avgResolutionHours,
        resolutionRate: totalTickets > 0 ? (resolvedTickets / totalTickets) * 100 : 0,
        // snake_case aliases the report framework's DefaultMetricsDisplay reads
        // (camelCase-only returns render a blank KPI row).
        total_count: totalTickets,
        open_tickets: openTickets,
        resolved_tickets: resolvedTickets,
      };
    },
    enabled: true,
  });
}
